import React, { useState, useEffect, useContext } from "react";
import {
  MapContainer,
  TileLayer,
  Polygon,
  Polyline,
  Marker,
  useMap,
  useMapEvents,
} from "react-leaflet";
import { useNavigate, useParams } from "react-router-dom";
import { useTranslation } from "react-i18next";
import L from "leaflet";
import "./Fields.css";
import { UserContext } from "../../UserContext";
import axios from "axios";
import toast from "react-hot-toast"; // Add this line
import CustomGeoSearch from "./CustomGeoSearch";

// --- Imports for Leaflet and its assets ---
import "leaflet/dist/leaflet.css";
// import "leaflet-draw/dist/leaflet.draw.css";
import icon from "leaflet/dist/images/marker-icon.png";
import iconShadow from "leaflet/dist/images/marker-shadow.png";

// --- FIX: Default Leaflet icon fix for compatibility ---
let DefaultIcon = L.icon({
  iconUrl: icon,
  shadowUrl: iconShadow,
  iconSize: [25, 41],
  iconAnchor: [12, 41],
});
L.Marker.prototype.options.icon = DefaultIcon;

// --- FEATURE: Custom Red Pin Icon ---
// We create a custom icon using SVG to make the drawing pins red.
const redIcon = new L.DivIcon({
  className: "custom-red-marker",
  html: `<svg viewBox="0 0 24 24" xmlns="http://www.w3.org/2000/svg" width="32" height="32" fill="#dc3545"><path d="M12 0C7.589 0 4 3.589 4 8c0 4.411 8 16 8 16s8-11.589 8-16c0-4.411-3.589-8-8-8zm0 12c-2.21 0-4-1.79-4-4s1.79-4 4-4 4 1.79 4 4-1.79 4-4 4z"/></svg>`,
  iconSize: [32, 32],
  iconAnchor: [16, 32],
  popupAnchor: [0, -32],
});

// Mock data for an existing field's boundaries when in "edit" mode
const mockFieldBoundaries = {
  1: [
    [31.2156, 29.9553],
    [31.2166, 29.9553],
    [31.2166, 29.9563],
    [31.2156, 29.9563],
  ],
};

// --- FEATURE: Intersection Prevention Logic ---
// Helper function to check if two line segments cross each other.
const segmentsIntersect = (p1, p2, p3, p4) => {
  const toPoint = (arr) => ({ x: arr[0], y: arr[1] });
  const p1_ = toPoint(p1),
    p2_ = toPoint(p2),
    p3_ = toPoint(p3),
    p4_ = toPoint(p4);
  const det =
    (p2_.x - p1_.x) * (p4_.y - p3_.y) - (p4_.x - p3_.x) * (p2_.y - p1_.y);
  if (det === 0) return false;
  const lambda =
    ((p4_.y - p3_.y) * (p4_.x - p1_.x) + (p3_.x - p4_.x) * (p4_.y - p1_.y)) /
    det;
  const gamma =
    ((p1_.y - p2_.y) * (p4_.x - p1_.x) + (p2_.x - p1_.x) * (p4_.y - p1_.y)) /
    det;
  return 0 < lambda && lambda < 1 && 0 < gamma && gamma < 1;
};

// --- FEATURE: Auto-Zoom for Editing ---
// Helper component to automatically fit the map to the given polygon bounds.
const AutoFitBounds = ({ bounds }) => {
  const map = useMap();
  useEffect(() => {
    if (bounds && bounds.length > 0) {
      map.fitBounds(bounds, { padding: [50, 50] });
    }
  }, [bounds, map]);
  return null;
};

// --- FIX: Helper Component to gracefully change the map's view ---
// This component gets the map instance and calls `setView` to pan to a new center,
// preventing the map from re-rendering and glitching.
const ChangeView = ({ center, zoom }) => {
  const map = useMap();
  useEffect(() => {
    map.setView(center, zoom);
  }, [center, zoom, map]);
  return null;
};

// Helper component to handle all map drawing events
const MapDrawingEvents = ({ points, onMapClick, onMarkerClick }) => {
  const { t } = useTranslation();
  useMapEvents({
    click(e) {
      const newPoint = [e.latlng.lat, e.latlng.lng];
      if (points.length > 1) {
        const lastPoint = points[points.length - 1];
        for (let i = 0; i < points.length - 2; i++) {
          const segP1 = points[i];
          const segP2 = points[i + 1];
          if (segmentsIntersect(lastPoint, newPoint, segP1, segP2)) {
            toast.error(t("Boundary lines cannot cross each other."));
            return; // Abort adding the point
          }
        }
      }
      onMapClick(e.latlng);
    },
  });

  // Render the markers for the points being drawn
  return points.map((point, index) => (
    <Marker
      key={index}
      position={point}
      icon={redIcon} // Uses the new red pin
      eventHandlers={
        index === 0 && points.length > 2
          ? {
              click: (e) => {
                L.DomEvent.stopPropagation(e); // Prevent map click from firing again
                onMarkerClick();
              },
            }
          : {}
      }
    />
  ));
};

const FieldMapEditor = () => {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const { fieldId } = useParams();
  const isEditMode = Boolean(fieldId);
  const { token } = useContext(UserContext);
  const defaultCenter = [31.205753, 29.924526];

  const [points, setPoints] = useState([]);
  const [boundaries, setBoundaries] = useState(null);
  const [isDrawing, setIsDrawing] = useState(true);
  const [mapCenter, setMapCenter] = useState(defaultCenter);
  const [mapZoom, setMapZoom] = useState(13);

  const parseCustomLocationString = (locationString) => {
    if (!locationString || typeof locationString !== "string") {
      return [];
    }
    // 1. Split the string by the hyphen '-' to get individual coordinate pairs
    const pairs = locationString.split("-");

    // 2. Map over each pair
    return pairs.map((pair) => {
      // 3. Split each pair by the comma ',' and convert the parts to numbers
      const coords = pair.split(",").map(Number);
      return coords;
    });
  };

  // Effect for loading existing mock boundaries in edit mode
  // useEffect(() => {
  //   if (isEditMode) {
  //     const existingBoundaries = mockFieldBoundaries[fieldId];
  //     if (existingBoundaries) {
  //       setBoundaries(existingBoundaries);
  //       setIsDrawing(false);
  //     }
  //   }
  // }, [fieldId, isEditMode]);

  // Effect for loading existing boundaries in edit mode
  useEffect(() => {
    // This is an async function to handle the API call
    const loadFieldBoundaries = async () => {
      if (isEditMode && fieldId && token) {
        try {
          // Fetch the field data from the backend
          const response = await axios.get(`api/field/${fieldId}`, {
            headers: {
              Authorization: `${token}`,
            },
          });

          if (response.data.items && response.data.items.length > 0) {
            const fieldData = response.data.items[0];
            // Use our robust parser to get the boundaries
            const existingBoundaries = parseCustomLocationString(
              fieldData.location
            );

            if (existingBoundaries && existingBoundaries.length > 0) {
              setBoundaries(existingBoundaries);
              setIsDrawing(false); // Switch to view/confirm mode
            }
          }
        } catch (error) {
          console.error("Failed to load field boundaries:", error);
          toast.error(t("Could not load the field boundaries for editing."));
          navigate(-1); // Go back to the previous page on error
        }
      }
    };

    loadFieldBoundaries();
  }, [fieldId, isEditMode, token, navigate, t]);

  // Effect for getting user's current location in add mode
  useEffect(() => {
    if (!isEditMode) {
      navigator.geolocation.getCurrentPosition(
        (position) => {
          setMapCenter([position.coords.latitude, position.coords.longitude]);
          setMapZoom(16); // Zoom in closer for current location
        },
        (error) => {
          console.error("Geolocation error:", error);
          setMapCenter(defaultCenter);
        }
      );
    }
  }, [isEditMode]);

  const handleMapClick = (latlng) => {
    if (!isDrawing) return;
    setPoints([...points, [latlng.lat, latlng.lng]]);
  };

  const handleCloseShape = () => {
    if (!isDrawing || points.length < 3) return;
    setBoundaries([...points, points[0]]);
    setPoints([]);
    setIsDrawing(false);
  };

  const handleContinue = () => {
    if (!boundaries) return;

    let finalBoundaries = [...boundaries];
    const firstPoint = finalBoundaries[0];
    const lastPoint = finalBoundaries[finalBoundaries.length - 1];

    // Check if the shape is closed
    if (firstPoint[0] !== lastPoint[0] || firstPoint[1] !== lastPoint[1]) {
      finalBoundaries.push(firstPoint); // Close the loop
    }

    const targetPath = isEditMode ? `/fields/${fieldId}/edit` : "/fields/new";
    navigate(targetPath, { state: { boundaries: finalBoundaries } });
  };

  const handleEditBoundaries = () => {
    setBoundaries(null);
    setPoints([]);
    setIsDrawing(true);
  };

  return (
    <div className="map-editor-container">
      <div className="map-editor-header">
        <h3>
          {isDrawing
            ? t("Draw Field Boundaries")
            : t("Field Boundaries Selected")}
        </h3>
        <p>
          {isDrawing
            ? t(
                "Click on the map to add points. Click the first pin to close the shape."
              )
            : t(
                "You can now edit your selection or continue to the next step."
              )}
        </p>
      </div>
      <MapContainer
        center={defaultCenter} // Initialize with a static default
        zoom={13}
        className="map-editor-map"
        // The problematic 'key' prop is removed to prevent glitches
      >
        {/* This component handles all dynamic view changes */}
        <ChangeView center={mapCenter} zoom={mapZoom} />

        <TileLayer
          url="https://server.arcgisonline.com/ArcGIS/rest/services/World_Imagery/MapServer/tile/{z}/{y}/{x}"
          attribution="&copy; Esri"
        />

        {/* search feature */}
        <CustomGeoSearch />

        {isDrawing && (
          <MapDrawingEvents
            points={points}
            onMapClick={handleMapClick}
            onMarkerClick={handleCloseShape}
          />
        )}

        {/* Shows an open line while drawing */}
        {points.length > 1 && (
          <Polyline
            positions={points}
            pathOptions={{ color: "#c7356c", dashArray: "5, 5" }}
          />
        )}

        {/* Shows the final, closed shape */}
        {boundaries && (
          <>
            <Polygon
              positions={boundaries}
              pathOptions={{ color: "#c7356c", dashArray: "5, 5" }}
            />
            <AutoFitBounds bounds={boundaries} />
          </>
        )}
      </MapContainer>

      {!isDrawing && boundaries && (
        <div className="map-editor-actions">
          <button className="button-secondary" onClick={handleEditBoundaries}>
            {t("Edit Selection")}
          </button>
          <button className="button-primary" onClick={handleContinue}>
            {t("Continue")}
          </button>
        </div>
      )}
    </div>
  );
};

export default FieldMapEditor;
